/**
 * Analytics tracking utility for PaperPal
 * Google Analytics style tracking with UTM support and session attribution
 */

import axios from 'axios';
import { API } from './api';

// Generate unique session ID
const generateSessionId = () => {
  return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('pp_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('pp_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || null,
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
    utm_term: urlParams.get('utm_term') || null,
    utm_content: urlParams.get('utm_content') || null
  };
};

// Classify traffic source (Google Analytics style)
const classifyTrafficSource = (utmSource, utmMedium, referrer) => {
  // If UTM source is provided, use it
  if (utmSource) {
    const source = utmSource.toLowerCase();
    const medium = (utmMedium || '').toLowerCase();
    
    // Social platforms
    if (['reddit', 'facebook', 'instagram', 'tiktok', 'twitter', 'linkedin', 'pinterest'].includes(source)) {
      return { source, medium: medium || 'social', type: 'social' };
    }
    // Email
    if (source === 'email' || medium === 'email') {
      return { source, medium: 'email', type: 'email' };
    }
    // Paid
    if (['cpc', 'ppc', 'paid', 'ads'].includes(medium)) {
      return { source, medium, type: 'paid' };
    }
    return { source, medium: medium || 'referral', type: 'referral' };
  }
  
  // Classify from referrer
  if (referrer) {
    const ref = referrer.toLowerCase();
    
    // Social platforms
    if (ref.includes('reddit.com')) return { source: 'reddit', medium: 'social', type: 'social' };
    if (ref.includes('facebook.com') || ref.includes('fb.com') || ref.includes('fb.me')) {
      return { source: 'facebook', medium: 'social', type: 'social' };
    }
    if (ref.includes('instagram.com')) return { source: 'instagram', medium: 'social', type: 'social' };
    if (ref.includes('tiktok.com')) return { source: 'tiktok', medium: 'social', type: 'social' };
    if (ref.includes('twitter.com') || ref.includes('x.com') || ref.includes('t.co')) {
      return { source: 'twitter', medium: 'social', type: 'social' };
    }
    if (ref.includes('linkedin.com')) return { source: 'linkedin', medium: 'social', type: 'social' };
    if (ref.includes('pinterest.com')) return { source: 'pinterest', medium: 'social', type: 'social' };
    
    // Search engines
    if (ref.includes('google.com') || ref.includes('google.ca')) {
      return { source: 'google', medium: 'organic', type: 'organic_search' };
    }
    if (ref.includes('bing.com')) return { source: 'bing', medium: 'organic', type: 'organic_search' };
    if (ref.includes('yahoo.com')) return { source: 'yahoo', medium: 'organic', type: 'organic_search' };
    if (ref.includes('duckduckgo.com')) return { source: 'duckduckgo', medium: 'organic', type: 'organic_search' };
    
    // Other referral
    try {
      const refUrl = new URL(referrer);
      return { source: refUrl.hostname, medium: 'referral', type: 'referral' };
    } catch {
      return { source: 'referral', medium: 'referral', type: 'referral' };
    }
  }
  
  // Direct traffic
  return { source: 'direct', medium: '(none)', type: 'direct' };
};

// Store session attribution on first visit
const storeSessionAttribution = () => {
  if (sessionStorage.getItem('pp_attribution')) {
    return; // Already stored for this session
  }
  
  const utmParams = getUTMParams();
  const referrer = document.referrer;
  const classification = classifyTrafficSource(utmParams.utm_source, utmParams.utm_medium, referrer);
  
  const attribution = {
    ...classification,
    campaign: utmParams.utm_campaign,
    term: utmParams.utm_term,
    content: utmParams.utm_content,
    referrer: referrer || null,
    landing_page: window.location.pathname,
    timestamp: new Date().toISOString()
  };
  
  sessionStorage.setItem('pp_attribution', JSON.stringify(attribution));
};

// Get stored attribution
const getAttribution = () => {
  const stored = sessionStorage.getItem('pp_attribution');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

// Get user info from localStorage (if logged in)
const getUserInfo = () => {
  try {
    const userStr = localStorage.getItem('paperpal_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return {
        user_id: user.user_id || null,
        user_plan: user.is_paid ? 'premium' : 'free',
        province: user.province || null
      };
    }
  } catch {
    // Ignore errors
  }
  return { user_id: null, user_plan: 'anonymous', province: null };
};

// Track event (non-blocking, fire-and-forget)
const trackEvent = async (eventType, metadata = {}) => {
  try {
    storeSessionAttribution();
    
    const attribution = getAttribution() || {};
    const userInfo = getUserInfo();
    
    const eventData = {
      event_type: eventType,
      session_id: getSessionId(),
      user_id: userInfo.user_id,
      user_plan: userInfo.user_plan,
      page: window.location.pathname,
      referrer_url: attribution.referrer || document.referrer || null,
      traffic_source: attribution.source || 'direct',
      traffic_medium: attribution.medium || '(none)',
      traffic_type: attribution.type || 'direct',
      campaign: attribution.campaign || null,
      landing_page: attribution.landing_page || window.location.pathname,
      device_type: getDeviceType(),
      province: userInfo.province,
      metadata
    };
    
    await axios.post(`${API}/analytics/track`, eventData);
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.debug('Analytics tracking failed:', error);
  }
};

// ==================== PUBLIC TRACKING FUNCTIONS ====================

// Track page view
export const trackPageView = (pageName = null) => {
  storeSessionAttribution();
  trackEvent('page_view', { page_name: pageName || window.location.pathname });
};

// Track CTA click
export const trackCTAClick = (ctaName = 'default', ctaLocation = null) => {
  trackEvent('cta_click', { cta_name: ctaName, cta_location: ctaLocation });
};

// Track checkout started
export const trackCheckoutStarted = (amount = null, plan = null) => {
  trackEvent('checkout_started', { amount, plan });
};

// Track purchase completed
export const trackPurchaseCompleted = (amount = null, transactionId = null) => {
  trackEvent('purchase_completed', { amount, transaction_id: transactionId });
};

// Track task completed
export const trackTaskCompleted = (taskId = null, taskTitle = null) => {
  trackEvent('task_completed', { task_id: taskId, task_title: taskTitle });
};

// Track profile updated
export const trackProfileUpdated = (fieldsUpdated = []) => {
  trackEvent('profile_updated', { fields_updated: fieldsUpdated });
};

// Track document upload started
export const trackDocumentUploadStarted = (documentType = null) => {
  trackEvent('document_upload_started', { document_type: documentType });
};

// Track document upload completed
export const trackDocumentUploadCompleted = (documentType = null, success = true) => {
  trackEvent('document_upload_completed', { document_type: documentType, success });
};

// Track guidance opened
export const trackGuidanceOpened = (guidanceType = null, context = null) => {
  trackEvent('guidance_opened', { guidance_type: guidanceType, context });
};

// Get current source (for display purposes)
export const getSource = () => {
  const attribution = getAttribution();
  return attribution?.source || 'direct';
};

// Export default object for convenience
const analytics = {
  trackPageView,
  trackCTAClick,
  trackCheckoutStarted,
  trackPurchaseCompleted,
  trackTaskCompleted,
  trackProfileUpdated,
  trackDocumentUploadStarted,
  trackDocumentUploadCompleted,
  trackGuidanceOpened,
  getSource
};

export default analytics;
